import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import './TrainingTooltip.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { setUIKeyValue } from '../../Actions/UIActions';
import { getUIKeyValueGeneral } from '../../Helpers/ui_helpers';

const ANIMATE_FADE_IN_DURATION = 400; // Sync with animation speed in SCSS

const TrainingTooltip = props => {
  /*
    Use this Tooltip if you want to train the user on a specific action.

    Example:
      <TrainingTooltip 
        visible={showTooltip} 
        trainingKey='unique-key-for-this-tooltip' 
        fadeInDelay={1_000} 
        fadeOutDelay={10_000}
        message={<>Some Custom <b>message</b> or string.</>} 
        position='top'
        numDismissalsToHide={1}
      >
        <div>Trigger Element</div>
      </TrainingTooltip>
  */

  const { message, visible: initialVisible } = props;

  // Visibility State
  const [visible, setVisible] = React.useState(false);

  // Props Passed In
  const [position, setPosition] = React.useState(props.position || 'top');
  const fadeInDelay = props.fadeInDelay || 1_000;
  const fadeOutDelay = props.fadeOutDelay || null;
  const numDismissalsToHide = props.numDismissalsToHide || 1;

  // Fade in the tooltip
  React.useEffect(() => {
    let timeoutId;
    timeoutId = setTimeout(() => setVisible(true), fadeInDelay);
    return () => timeoutId && clearTimeout(timeoutId);
  }, []);

  // Fade out the tooltip after the fade out delay duration
  React.useEffect(() => {
    let timeoutId;
    if (fadeOutDelay) timeoutId = setTimeout(() => setVisible(false), fadeOutDelay);
    return () => timeoutId && clearTimeout(timeoutId);
  }, []);

  // Additional Classes
  const additionalClasses = {
    ...(props.additionalClasses || {}),
    visible,
    [position]: true
  };

  // Handled Dismissals
  const dismissalsMap = getUIKeyValueGeneral(props.ui, props.trainingKey, {});
  const numDismissals = dismissalsMap[props.trainingKey] || 0;
  const dismissTooltip = () => {
    props.trainingKey && visible && props.setUIKeyValue(props.trainingKey, { ...dismissalsMap, [props.trainingKey]: numDismissalsToHide + 1 });
    setVisible(false);
    setTimeout(() => setDismissed(true), ANIMATE_FADE_IN_DURATION);
  };
  const hasBeenDismissedTooManyTimes = numDismissals >= numDismissalsToHide;
  const [dismissed, setDismissed] = React.useState(hasBeenDismissedTooManyTimes);

  // Handle directionality in the case that it can't fit on the screen
  const tooltipWrapperRef = React.useRef(null);
  const [offsetTransformX, setOffsetTransformX] = React.useState(0);

  React.useEffect(() => {
    if (tooltipWrapperRef.current) {
      const tooltipElement = tooltipWrapperRef.current;
      const { width, height, left, top } = tooltipElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Check if tooltip is off-screen and reposition if needed
      const isOffScreen = left < 0 || left + width > viewportWidth || top < 0 || top + height > viewportHeight;

      if (isOffScreen) {
        // Use preferred position if specified, otherwise use automatic logic
        if (props.positionIfInitialOffScreen) {
          setPosition(props.positionIfInitialOffScreen);
        } else {
          // Automatic repositioning logic
          if (left < 0) {
            // Off left side - move to right
            setPosition('right');
          } else if (left + width > viewportWidth) {
            // Off right side - move to left
            setPosition('left');
          } else if (top < 0) {
            // Off top - move to bottom
            setPosition('bottom');
          } else if (top + height > viewportHeight) {
            // Off bottom - move to top
            setPosition('top');
          }
        }
      }

      // If we are on the right or left side, check if we can fit on the screen
      if (position === 'top' || position === 'bottom') {
        // Create a ResizeObserver to watch for any size/position changes
        const resizeObserver = new ResizeObserver(() => {
          setTimeout(() => {
            const { width, left } = tooltipElement.getBoundingClientRect();
            const pixelsTooFarRight = left + width > viewportWidth ? left + width - viewportWidth + 20 : 0;
            const pixelsTooFarLeft = left < 20 ? Math.abs(left) : 0;
            setOffsetTransformX(
              offsetTransformX + (pixelsTooFarRight > 0 ? -1 * pixelsTooFarRight : pixelsTooFarLeft > 0 ? 1 * pixelsTooFarLeft : 0)
            );
          }, _.max([props.fadeInDelay - ANIMATE_FADE_IN_DURATION, 0]));
        });

        resizeObserver.observe(tooltipElement);
        return () => resizeObserver.disconnect();
      }
    }
  }, [position]);

  // If the tooltip is not visible, return the children
  if (!initialVisible) return props.children;

  // Render the tooltip
  return (
    <div className={cn('training-tooltip-outer-container')}>
      {props.children}
      {!dismissed && (
        <div className={cn('training-tooltip-wrapper-outer', additionalClasses)}>
          <div style={{ transform: `translateX(${offsetTransformX}px)` }}>
            <div ref={tooltipWrapperRef} onClick={dismissTooltip} className={cn('training-tooltip-wrapper', additionalClasses)}>
              <div
                style={{ transform: `translateX(${-1 * offsetTransformX}px)` }}
                className={cn('training-tooltip-wrapper-caret', additionalClasses)}
              />
              <div className={cn('training-tooltip-content', additionalClasses)}>
                <div className='training-tooltip-message'>{message}</div>
                <div className='training-tooltip-dismiss-button'>
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

TrainingTooltip.propTypes = {
  // Required
  visible: PropTypes.bool.isRequired, // If true, show the tooltip, otherwise pass through the children
  children: PropTypes.object.isRequired,
  trainingKey: PropTypes.string.isRequired, // Unique key for the tooltip to ensure we don't over-render the same tooltip multiple times

  // Optional
  position: PropTypes.string, // top (default), bottom, left, right
  fadeInDelay: PropTypes.number, // delay in ms before the tooltip is shown, default is 1000
  fadeOutDelay: PropTypes.number, // delay in ms before the tooltip is hidden, default is null which means it will not fade out
  numDismissalsToHide: PropTypes.number, // Number of dismissals before the tooltip is shown, default is 1

  // Advanced Optional
  positionIfInitialOffScreen: PropTypes.string, // top (default), bottom, left, right

  // From Inside
  ui: PropTypes.object.isRequired,
  setUIKeyValue: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { ui } = state;
  return { ui };
};

export default connect(mapStateToProps, {
  setUIKeyValue
})(TrainingTooltip);
